import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle, useContext } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import type { GetRef, InputRef, TableColumnsType, TableColumnType, FormInstance } from 'antd';
import { Button, Input, Popconfirm, Space, Table, Form, Modal } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { Resizable } from 'react-resizable';
import type { ResizeCallbackData } from 'react-resizable';
import { useAuth } from "../../src/context/AuthContext";
import axios from 'axios';
import { useNavigate, useSearchParams } from "react-router-dom";


type EditableFormInstance<T> = GetRef<typeof Form<T>>;
const EditableContext = React.createContext<EditableFormInstance<any> | null>(null);

interface EditableRowProps {
    index: number;
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};

interface EditableCellProps {
    title: React.ReactNode;
    editable: boolean;
    dataIndex: keyof ForemanDataType;
    record: ForemanDataType;
    handleSave: (record: ForemanDataType) => void;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
    title,
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    ...restProps
}) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef<InputRef>(null);
    const form = useContext(EditableContext)!;

    useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
        }
    }, [editing]);

    const toggleEdit = () => {
        setEditing(!editing);
        form.setFieldsValue({ [dataIndex]: record[dataIndex] });
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            toggleEdit();
            handleSave({ ...record, ...values });
        } catch (errInfo) {
            console.log('Save failed:', errInfo);
        }
    };

    let childNode = children;

    if (editable) {
        childNode = editing ? (
            <Form.Item
                style={{ margin: 0 }}
                name={dataIndex}
                rules={[{ required: true, message: `${title} is required.` }]}
            >
                <Input ref={inputRef} onPressEnter={save} onBlur={save} />
            </Form.Item>
        ) : (
            <div
                className="editable-cell-value-wrap"
                style={{ paddingInlineEnd: 24, minHeight: '32px' }}
                onClick={toggleEdit}
            >
                {children}
            </div>
        );
    }

    return <td {...restProps}>{childNode}</td>;
};


interface ForemanDataTableProps {
    form: FormInstance;
    autosaveStatus: string;
    setAutosaveStatus: (status: string) => void;
}

interface ForemanDataType {
    id: string;
    Name: string;
    Code: string;
    Warehouse: string;
    WarehouseCode?: string;
}

type ForemanDataIndex = keyof ForemanDataType;

const ResizableTitle = (props: React.HTMLAttributes<any> & { onResize: (e: React.SyntheticEvent, data: ResizeCallbackData) => void; width: number; onDoubleClick: () => void; }) => {
    const { onResize, width, onDoubleClick, ...restProps } = props;

    if (!width) {
        return <th {...restProps} onDoubleClick={onDoubleClick} />;
    }

    return (
        <Resizable
            width={width}
            height={0}
            handle={
                <span
                    className="react-resizable-handle"
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                />
            }
            onResize={onResize}
            draggableOpts={{ enableUserSelectHack: false }}
        >
            <th {...restProps} onDoubleClick={onDoubleClick} />
        </Resizable>
    );
};

const ForemanDataTable = forwardRef<any, ForemanDataTableProps>(({ form, autosaveStatus, setAutosaveStatus }, ref) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { userData } = useAuth();
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [dataSource, setDataSource] = useState<ForemanDataType[]>([]);
    const [foremanId, setForemanId] = useState<string | null>(null);
    const formValues = Form.useWatch([], form);
    const foremanIdFromUrl = searchParams.get('id');
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const originalCodeRef = useRef<string>("");
    const warehouseCodeRef = useRef<string>("");

    useImperativeHandle(ref, () => ({
        handleAdd: () => {

            const newData: ForemanDataType = {
                id: Date.now().toString(),
                Name: "",
                Code: "",
                Warehouse: userData?.warehouse_name || "",
                WarehouseCode: userData?.warehouse_code || "",
            };
            setDataSource([...dataSource, newData]);
        },
        handleNew: () => {
            setDataSource([]);
            setForemanId(null);
            setAutosaveStatus('stopped');
            form.resetFields();
            originalCodeRef.current = "";
            warehouseCodeRef.current = userData?.warehouse_code || "";
            form.setFieldsValue({
                status: "Active",
                warehouse: userData?.warehouse_name || ""
            });
            navigate('/dashboard/foreman_data');
        },
        handleGenerateNew: async () => {
            setDataSource([]);
            setForemanId(null);
            setAutosaveStatus('stopped');
            form.resetFields();
            originalCodeRef.current = "";
            warehouseCodeRef.current = userData?.warehouse_code || "";
            let nextCode = "1001";
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/foreman/list`);
                if (Array.isArray(response.data)) {
                    const codes = response.data
                        .map((item: any) => parseInt(item.code, 10))
                        .filter((num: number) => !isNaN(num) && num >= 1001);
                    if (codes.length > 0) {
                        nextCode = (Math.max(...codes) + 1).toString();
                    }
                }
            } catch (error) {
                console.error("Error generating code:", error);
            }
            form.setFieldsValue({
                status: "Active",
                warehouse: userData?.warehouse_name || "",
                code: nextCode
            });
            navigate('/dashboard/foreman_data');
        },
        handleDeactive: () => {
            form.setFieldsValue({ status: "Deactive" });
            performAutosave(true);
        },
        handleActive: () => {
            form.setFieldsValue({ status: "Active" });
            performAutosave();
        },
        setForemanId: (id: string) => {
            setForemanId(id);
        }
    }));

    useEffect(() => {
        const fetchExistingData = async () => {
            if (foremanIdFromUrl) {
                try {
                    setAutosaveStatus('processing');

                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/foreman/detail`, {
                        params: { id: foremanIdFromUrl }
                    });

                    const data = response.data;

                    warehouseCodeRef.current = data.warehouse;
                    form.setFieldsValue({
                        status: data.status,
                        warehouse: data.warehouse_name || data.warehouse,
                        code: data.code,
                        name: data.name
                    });
                    originalCodeRef.current = data.code;

                    const formattedMechanics = data.mechanics.map((mech: any) => ({
                        id: mech.id.toString(),
                        Name: mech.name,
                        Code: mech.code,
                        Warehouse: mech.warehouse === userData?.warehouse_code ? userData?.warehouse_name : mech.warehouse,
                        WarehouseCode: mech.warehouse
                    }));

                    setDataSource(formattedMechanics);
                    setForemanId(data.id);
                    setAutosaveStatus('idle');
                } catch (error) {
                    console.error("Error loading foreman data", error);
                    setAutosaveStatus('error');
                }
            }
        };

        fetchExistingData();
    }, [foremanIdFromUrl, form, setAutosaveStatus]);

    const performAutosave = async (isDeactivating = false) => {
        const codeValue = form.getFieldValue('code');
        const statusValue = form.getFieldValue('status');

        if (!codeValue) {
            setAutosaveStatus('stopped');
            return;
        }

        if (foremanId && codeValue !== originalCodeRef.current && !isDeactivating) {
            setIsConfirmModalOpen(true);
            setAutosaveStatus('stopped');
            return;
        }

        if (statusValue !== 'Active' && !isDeactivating) {
            setAutosaveStatus('stopped');
            return;
        }

        setAutosaveStatus('processing');

        try {
            const mechanicCodeString = dataSource
                .map(item => item.Code)
                .filter(code => code !== "")
                .join(';');

            const payload = {
                id: foremanId,
                status: form.getFieldValue('status'),
                warehouse: warehouseCodeRef.current,
                code: codeValue,
                name: form.getFieldValue('name'),
                mechanic_code: mechanicCodeString,
                mechanics: dataSource.map(item => ({
                    name: item.Name,
                    code: item.Code,
                    warehouse: item.WarehouseCode || item.Warehouse,
                    foreman_code: codeValue
                }))
            };

            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/foreman/autosave`, payload);

            if (response.data && response.data.id) {
                setForemanId(response.data.id);
                if (response.data.code) {
                    originalCodeRef.current = response.data.code;
                } else if (!foremanId) {
                    originalCodeRef.current = codeValue;
                }
            } else if (payload.code) {
                originalCodeRef.current = payload.code;
            }

            setAutosaveStatus('idle');
        } catch (error) {
            console.error("Autosave Error:", error);
            setAutosaveStatus('error');
        }
    };

    const handleCancelChangeCode = () => {
        setIsConfirmModalOpen(false);
        form.setFieldValue("code", originalCodeRef.current);
        setAutosaveStatus('stopped');
    };

    const handleConfirmChangeCode = () => {
        const currentCode = form.getFieldValue("code");
        setIsConfirmModalOpen(false);
        originalCodeRef.current = currentCode;
        performAutosave();
    };

    useEffect(() => {
        setAutosaveStatus('processing');
        const delayDebounceFn = setTimeout(() => {
            if (formValues?.code) {
                performAutosave();
            } else {
                setAutosaveStatus('stopped');
            }
        }, 800);

        return () => clearTimeout(delayDebounceFn);
    }, [formValues, dataSource]);


    const handleSave = (row: ForemanDataType) => {
        const newData = [...dataSource];
        const index = newData.findIndex((item) => row.id === item.id);
        const item = newData[index];
        const updatedRow = { ...item, ...row };
        if (updatedRow.Name && !updatedRow.Code) {
            let nextCodeNum = 101;
            const existingCodes = newData
                .map((d) => parseInt(d.Code, 10))
                .filter((num) => !isNaN(num) && num >= 101);
            while (existingCodes.includes(nextCodeNum)) {
                nextCodeNum++;
            }
            updatedRow.Code = nextCodeNum.toString();
        }
        newData.splice(index, 1, updatedRow);
        setDataSource(newData);
    };

    useEffect(() => {
        if (form && !foremanIdFromUrl) {
            warehouseCodeRef.current = userData?.warehouse_code || "";

            form.setFieldsValue({
                status: "Active",
                warehouse: userData?.warehouse_name || ""
            });
        } else if (form && foremanIdFromUrl) {
            form.setFieldsValue({
                warehouse: userData?.warehouse_name || form.getFieldValue("warehouse")
            });
        }
    }, [form, userData, foremanIdFromUrl]);

    const searchInput = useRef<InputRef>(null);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: ForemanDataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: ForemanDataIndex): TableColumnType<ForemanDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            (record[dataIndex] ?? "")
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const [columns, setColumns] = useState<(TableColumnType<ForemanDataType> & { editable?: boolean })[]>([
        {
            title: 'Name Mechanic',
            dataIndex: 'Name',
            key: 'Name',
            width: 200,
            editable: true,
            ...getColumnSearchProps('Name'),
            sorter: (a, b) => a.Name.localeCompare(b.Name),
            sortDirections: ['descend', 'ascend'],
            ellipsis: true,
        },
        {
            title: 'Code Mechanic',
            dataIndex: 'Code',
            key: 'Code',
            width: 150,
            editable: true,
            ...getColumnSearchProps('Code'),
            sorter: (a, b) => a.Code.localeCompare(b.Code),
            sortDirections: ['descend', 'ascend'],
            ellipsis: true,
        },
        {
            title: 'Warehouse',
            dataIndex: 'Warehouse',
            key: 'Warehouse',
            width: 150,
            ...getColumnSearchProps('Warehouse'),
            sorter: (a, b) => a.Warehouse.localeCompare(b.Warehouse),
            sortDirections: ['descend', 'ascend'],
            ellipsis: true,
        },
        {
            title: 'Action',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Popconfirm title="Sure to delete?" onConfirm={() => {
                    setDataSource((prevDataSource) => prevDataSource.filter((item) => item.id !== record.id));
                }}>
                    <Button type="link" danger>Delete</Button>
                </Popconfirm>
            ),
        },
    ]);


    const handleResize = (index: number) => (
        e: React.SyntheticEvent,
        { size }: ResizeCallbackData
    ) => {
        setColumns((prevColumns) => {
            const newColumns = [...prevColumns];
            newColumns[index] = {
                ...newColumns[index],
                width: size.width,
            };
            return newColumns;
        });
    };

    const autoSizeColumn = (dataIndex: ForemanDataIndex) => {
        if (!dataSource || dataSource.length === 0) return;
        const FONT_STYLE = "14px 'Poppins', sans-serif";
        const PADDING = 32;

        const getTextWidth = (text: string, font: string): number => {
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            if (context) {
                context.font = font;
                return context.measureText(text).width;
            }
            return 0;
        };

        const getRenderedText = (record: ForemanDataType, dataIndex: ForemanDataIndex): string => {
            const value = record[dataIndex];
            if (!value) return '';
            return String(value);
        };

        const headerColumn = columns.find(col => col.key === dataIndex);
        const headerText = headerColumn && headerColumn.title ? String(headerColumn.title) : '';
        const headerWidth = getTextWidth(headerText, `600 ${FONT_STYLE}`) + PADDING;
        const cellWidths = dataSource.map((item: ForemanDataType) => {
            const cellText = getRenderedText(item, dataIndex);
            return getTextWidth(cellText, FONT_STYLE) + PADDING;
        });

        const maxContentWidth = Math.ceil(Math.max(headerWidth, ...cellWidths, 50));

        setColumns(prevColumns => {
            const index = prevColumns.findIndex(col => col.key === dataIndex);
            if (index === -1) return prevColumns;
            const newColumns = [...prevColumns];
            newColumns[index] = {
                ...newColumns[index],
                width: maxContentWidth,
            };
            return newColumns;
        });
    };

    const components = {
        header: {
            cell: ResizableTitle,
        },

        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    };

    const resizableColumns = columns.map((col, index) => {

        if (!col.editable) {
            return {
                ...col,
                onHeaderCell: (column: any) => ({
                    width: column.width,
                    onResize: handleResize(index),
                    onDoubleClick: () => autoSizeColumn(column.dataIndex as ForemanDataIndex),
                }),
            };
        }
        return {
            ...col,
            onHeaderCell: (column: any) => ({
                width: column.width,
                onResize: handleResize(index),
                onDoubleClick: () => autoSizeColumn(column.dataIndex as ForemanDataIndex),
            }),
            onCell: (record: ForemanDataType) => ({
                record,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
                handleSave,
            }),
        };
    });

    const tableWidth = columns.reduce((sum, col) => sum + (col.width as number || 0), 0);

    return (
        <>
            <Modal
                title="Konfirmasi Perubahan"
                open={isConfirmModalOpen}
                centered
                onOk={handleConfirmChangeCode}
                onCancel={handleCancelChangeCode}
                width={400}
                footer={[
                    <Button key="no" onClick={handleCancelChangeCode}>
                        TIdak
                    </Button>,
                    <Button key="yes" type="primary" onClick={handleConfirmChangeCode}>
                        YA
                    </Button>,
                ]}
                className='custom-modal-warning'
            >
                <div>
                    Apakah yakin untuk mengubah kode Foreman? kode yang di ubah akan mempengaruhi transaksi pada service yang sudah terjadi
                </div>
            </Modal>
            <Table<ForemanDataType>
                bordered
                components={components}
                rowClassName={() => 'editable-row'}
                columns={resizableColumns as any}
                dataSource={dataSource}
                scroll={{ x: tableWidth }}
                rowKey="id"
            />
        </>
    );
});

export default ForemanDataTable;